import { ViewChild, Output, OnInit, OnDestroy, forwardRef } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import { AnonymousSubscription } from 'rxjs/Subscription';
import { assign } from 'lodash';

import { EntityBase } from '~/core/model';
import { ColumnDefinition, GridConfig, GridModel } from './Grids';
import { GridComponent } from './grid.component';
import { SIZES } from '~/shared/configs';

export interface RowSelection<T> {
  data: T;
  isSelected: boolean;
}

export interface SelectionEvent {
  id: number;
  isSelected: false;
}

export abstract class GridBaseComponent<T = any> implements OnDestroy, OnInit {
  @ViewChild(forwardRef(() => GridComponent)) grid: GridComponent<T>;
  @Output() focusItem: Subject<T> = new Subject();

  data: Observable<GridModel<T>>;
  gridOptions: Observable<GridConfig>;
  readonly columnDefinitions: BehaviorSubject<ColumnDefinition[]> = new BehaviorSubject([]);

  protected subjects: Subject<any>[] = [];
  protected subscriptions: AnonymousSubscription[] = [];

  constructor() {
    this.subjects.push(this.columnDefinitions, this.focusItem);
  }

  ngOnInit() {
    if (!this.gridOptions) this.gridOptions = Observable.of({});
  }

  ngOnDestroy() {
    if (this.columnDefinitions) this.columnDefinitions.complete();

    this.subjects.forEach(s => s.complete());
    this.subjects = [];

    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
  }

  createColumn(label: string, property?: string, type?: string, width: number = 40, params?: ColumnDefinition): ColumnConfiguration {
    return new ColumnConfiguration(label, property, type, width, params);
  }

  setSortModel(colId, sortDirection) {
    this.grid._gridOptions.api.setSortModel([{ colId: colId, sort: sortDirection }]);
  }

  setRowData(rowId, columnKey, value) {
    const row = this.grid._gridOptions.api.getRowNode(rowId);
    row.setDataValue(columnKey, value);
  }

  refreshInMemoryRowModel(step) { /* step can be -> group, filter, map, aggregate, sort, pivot */
    this.grid._gridOptions.api.refreshInMemoryRowModel(step);
  }

  refreshRows(rowIds: any[]) {
    this.grid._gridOptions.api.refreshRows(rowIds.map(id => this.grid._gridOptions.api.getRowNode(id)));
  }
}

export class SelectableGridBaseComponent<T extends EntityBase = EntityBase>
  extends GridBaseComponent<RowSelection<T>>
  implements OnDestroy {
  @Output() selectionChanged: Subject<RowSelection<T>[]> = new Subject<RowSelection<T>[]>();

  ngOnDestroy() {
    super.ngOnDestroy();
    this.selectionChanged.complete();
  }

  createColumn(label: string, property?: string, type?: string, width: number = 40, params?: ColumnDefinition): ColumnConfiguration {
    return super.createColumn(label, `data.${property}`, type, width, params);
  }

  createSelectionColumn(): ColumnConfiguration {
    return super.createColumn('', 'isSelected', null, 40)
      .useSorting(false);
  }

  selectRow(id: any): void {
    const row = this.grid._gridOptions.api.getRowNode(id);
    if (row) {
      row.setSelected(true);
      this.grid._gridOptions.api.ensureIndexVisible(row.rowIndex);
    }
  }
}

export class ColumnConfiguration implements ColumnDefinition {
  clickEventSuppressed?: boolean;
  cellClass?: string;
  headerClass?: string;
  columns?: ColumnDefinition[];
  sortable?: boolean;
  format?: string;
  decimals?: number;
  height?: number;
  paddingLeft?: string;
  paddingRight?: string;
  template?: string;
  noData?: string;
  comparator?: any;
  cellRenderer?: any;
  cellRendererFramework?: any;
  hidden?: boolean;
  pinned?: boolean;

  constructor(
    public label: string,
    public property: string,
    public type: string,
    public width = 40,
    params?: ColumnDefinition) {
    assign(this, params || {});
  }

  pin(pinned: boolean = true): ColumnConfiguration {
    this.pinned = pinned;
    return this;
  }

  hide(hidden: boolean = true): ColumnConfiguration {
    this.hidden = hidden;
    return this;
  }

  useSorting(sort: boolean | Function = true): ColumnConfiguration {
    if (typeof sort === 'function') this.comparator = sort;
    this.sortable = !!sort;
    return this;
  }

  characterWidth(chars: number): ColumnConfiguration {
    this.width = SIZES.characterWidth(chars);
    return this;
  }

  characterHeight(chars: number): ColumnConfiguration {
    this.height = SIZES.characterWidth(chars);
    return this;
  }

  suppressClickEvent(clickEventSuppressed: boolean): ColumnConfiguration {
    this.clickEventSuppressed = clickEventSuppressed;
    return this;
  }

  useComponent(component: Function): ColumnConfiguration {
    this.cellRendererFramework = component;
    return this;
  }

  useCellClass(cls: string): ColumnConfiguration {
    this.cellClass = cls;
    return this;
  }

  useHeaderClass(cls: string): ColumnConfiguration {
    this.headerClass = cls;
    return this;
  }

  addChildren(columns: ColumnDefinition[]): ColumnConfiguration {
    if (!this.columns) this.columns = columns;
    else this.columns = this.columns.concat(columns);
    return this;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/grids/grid-base.component.ts